import api from '../core/api.js';
import eventBus from '../core/eventBus.js';

// Skip the star on these path prefixes (auth / operator routes)
const SKIP_PREFIXES = ['/login', '/forgot-password', '/reset-password', '/operator'];

// In-memory cache shared by all callers
const cache = {
  items: [],      // [{ id, title, url }]
  loaded: false,
  promise: null,

  async ensure() {
    if (this.loaded) return;
    if (this.promise) return this.promise;
    this.promise = api.get('/bookmarks', {}, { silent: true })
      .then(r => { this.items = r?.data || []; this.loaded = true; })
      .catch(() => { this.loaded = true; });
    return this.promise;
  },

  invalidate() { this.loaded = false; this.promise = null; },

  isFav(url)   { return this.items.some(b => b.url === url); },
  find(url)    { return this.items.find(b => b.url === url); },
  push(item)   { this.items.push(item); },
  remove(id)   { this.items = this.items.filter(b => b.id !== id); },
};

// Patch history.pushState / replaceState to emit 'url:changed' so we can
// refresh the star when a page updates query params without a full route
// navigation (e.g. applying a saved list-view filter).
function _patchHistory() {
  const _emit = () => eventBus.emit('url:changed');
  for (const method of ['pushState', 'replaceState']) {
    const orig = history[method];
    history[method] = function (...args) {
      const result = orig.apply(this, args);
      _emit();
      return result;
    };
  }
  window.addEventListener('popstate', _emit);
}

// Salesforce-style header favorite star — a single static button in the
// top header bar (#header-fav-star, placed before the notification bell
// in index.html) that reflects/toggles whether the current URL is a
// Favorite (pathname + search, so saved list-view URLs are supported).
class FavoriteStar {
  constructor() {
    this._btn = null;
    this._currentUrl = null;
    this._busy = false;
  }

  init() {
    this._btn = document.getElementById('header-fav-star');
    if (!this._btn) return;

    _patchHistory();

    this._btn.addEventListener('click', (e) => {
      e.preventDefault();
      this._toggle();
    });

    cache.ensure().then(() => this._refresh());

    eventBus.on('route:changed', () => this._onUrlChange());
    eventBus.on('url:changed',   () => this._onUrlChange());
    this._onUrlChange();
  }

  _skip(path) {
    return SKIP_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
  }

  _onUrlChange() {
    const full = window.location.pathname + window.location.search;
    this._currentUrl = full;
    this._refresh();
  }

  _refresh() {
    if (!this._btn) return;
    const url = this._currentUrl || (window.location.pathname + window.location.search);

    if (this._skip(window.location.pathname)) {
      this._btn.style.display = 'none';
      return;
    }
    this._btn.style.display = '';
    this._paintBtn(cache.isFav(url));
  }

  _paintBtn(isFav) {
    this._btn.innerHTML = isFav
      ? `<i class="fas fa-star" style="color:#f59e0b;" aria-hidden="true"></i>`
      : `<i class="far fa-star" aria-hidden="true"></i>`;
    this._btn.classList.toggle('text-gray-400', !isFav);
    this._btn.setAttribute('title', isFav ? 'Remove from Favorites' : 'Add to Favorites');
    this._btn.setAttribute('aria-label', isFav ? 'Remove from Favorites' : 'Add to Favorites');
  }

  async _toggle() {
    if (this._busy) return;
    this._busy = true;

    const url = this._currentUrl || (window.location.pathname + window.location.search);
    const wasFav = cache.isFav(url);

    // Optimistic UI update + pulse animation
    this._paintBtn(!wasFav);
    this._btn.style.transform = 'scale(1.3)';
    setTimeout(() => { this._btn.style.transform = ''; }, 180);

    try {
      if (wasFav) {
        const bm = cache.find(url);
        if (bm) {
          await api.delete(`/bookmarks/${bm.id}`);
          cache.remove(bm.id);
        }
        window.notify?.success('Removed from Favorites');
      } else {
        // Title: page h1 text, or clean up the path as fallback
        const h1 = document.getElementById('main-content')?.querySelector('h1');
        const title = h1?.textContent?.trim()
          || window.location.pathname.split('/').filter(Boolean)
               .map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
          || 'Page';

        const res = await api.post('/bookmarks', {
          title,
          url,           // includes search params if present
          icon: 'fa-star',
          color: 'yellow',
          sortOrder: cache.items.length,
        });
        cache.push(res.data);
        window.notify?.success('Added to Favorites');
      }
    } catch {
      // Revert on error
      this._paintBtn(wasFav);
    } finally {
      this._busy = false;
    }
  }
}

// Export singleton — one instance manages the header star
export const favoriteStar = new FavoriteStar();
export default favoriteStar;
